import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import {
    IRCAComplianceByMill,
    IRCAAPComplianceEntity,
    IActionPlanComplianceByMill,
    IRCAComplianceByResponsible,
    IAPComplianceByResponsible,
    IAPAvgPerUser,
    IRcaMethodologyUsedByUser,
    IArgsFilters,
} from "./RCAAPComplianceEntityInterfaces";
import {
    getAPComplianceByResponse,
    getActionPlanComplianceByMill,
    getAvgActionPlansPerUserResponse,
    getRCAComplianceByMill,
    getRCAComplianceByResponsible,
    getRcaMethodologyUsedByUserResponse,
} from "api/queries/RCA/RCAComplianceAPI";
import { notSelected } from "constants/constants";

export const RCAAPComplianceEntity: IRCAAPComplianceEntity = {
    GetRCAComplianceByMill: (filters: IArgsFilters) => {
        const { loading, error, data } = useQuery(getRCAComplianceByMill, {
            fetchPolicy: "no-cache",
            variables: {
                args: {
                    input_date: filters.date,
                    ...(filters?.meetingTypeIds &&
                        !filters.meetingTypeIds.includes(notSelected) &&
                        filters.totalMeetingTypes &&
                        filters.meetingTypeIds.length !== filters.totalMeetingTypes && {
                            meeting_type_ids: `{${filters.meetingTypeIds}}`,
                        }),
                    ...(filters?.meetingTypeIds &&
                        filters.meetingTypeIds.includes(notSelected) && {
                            meeting_type_null: true,
                        }),
                },
            },
        });

        const [rcaComplianceByMill, setRcaComplianceByMill] = useState<IRCAComplianceByMill[]>([]);

        useEffect(() => {
            if (data) {
                setRcaComplianceByMill(data.rca_compliance_by_mill);
            }
        }, [data]);

        return {
            loading,
            rcaComplianceByMill,
            apolloQueryError: error,
        };
    },
    GetRCAComplianceByResponsible: (filters: IArgsFilters) => {
        const { loading, error, data } = useQuery(getRCAComplianceByResponsible, {
            fetchPolicy: "no-cache",
            variables: {
                args: {
                    input_date: filters.date,
                    ...(filters?.meetingTypeIds &&
                        !filters.meetingTypeIds.includes(notSelected) &&
                        filters.totalMeetingTypes &&
                        filters.meetingTypeIds.length !== filters.totalMeetingTypes && {
                            meeting_type_ids: `{${filters.meetingTypeIds}}`,
                        }),
                    ...(filters?.meetingTypeIds &&
                        filters.meetingTypeIds.includes(notSelected) && {
                            meeting_type_null: true,
                        }),
                },
            },
        });

        const [rcaComplianceByResponsible, setRcaComplianceByResponsible] = useState<IRCAComplianceByResponsible[]>([]);

        useEffect(() => {
            if (data) {
                setRcaComplianceByResponsible(data.rca_compliance_totals_by_responsible);
            }
        }, [data]);

        return {
            loading,
            rcaComplianceByResponsible,
            apolloQueryError: error,
        };
    },
    GetActionPlanComplianceByMill: (filters: IArgsFilters) => {
        const { loading, error, data } = useQuery(getActionPlanComplianceByMill, {
            variables: {
                args: {
                    input_date: filters.date,
                    ...(filters?.meetingTypeIds &&
                        !filters.meetingTypeIds.includes(notSelected) &&
                        filters.totalMeetingTypes &&
                        filters.meetingTypeIds.length !== filters.totalMeetingTypes && {
                            meeting_type_ids: `{${filters.meetingTypeIds}}`,
                        }),
                    ...(filters?.meetingTypeIds &&
                        filters.meetingTypeIds.includes(notSelected) && {
                            meeting_type_null: true,
                        }),
                },
            },
        });

        const [actionPlanComplianceByMill, setActionPlanComplianceByMill] = useState<IActionPlanComplianceByMill[]>([]);

        useEffect(() => {
            if (data) {
                setActionPlanComplianceByMill(data.action_plan_compliance_by_mill);
            }
        }, [data]);

        return {
            loading,
            actionPlanComplianceByMill,
            apolloQueryError: error,
        };
    },
    GetAPComplianceByResponsible: (filters: IArgsFilters) => {
        const { loading, error, data } = useQuery(getAPComplianceByResponse, {
            fetchPolicy: "no-cache",
            variables: {
                args: {
                    input_date: filters.date,
                    ...(filters?.meetingTypeIds &&
                        !filters.meetingTypeIds.includes(notSelected) &&
                        filters.totalMeetingTypes &&
                        filters.meetingTypeIds.length !== filters.totalMeetingTypes && {
                            meeting_type_ids: `{${filters.meetingTypeIds}}`,
                        }),
                    ...(filters?.meetingTypeIds &&
                        filters.meetingTypeIds.includes(notSelected) && {
                            meeting_type_null: true,
                        }),
                },
            },
        });

        const [apComplianceByResponsible, setApComplianceByResponsible] = useState<IAPComplianceByResponsible[]>([]);

        useEffect(() => {
            if (data) {
                setApComplianceByResponsible(data.action_plan_compliance_totals_by_responsible);
            }
        }, [data]);

        return {
            loading,
            apComplianceByResponsible,
            apolloQueryError: error,
        };
    },
    GetAPAvgPerUser: (filters: IArgsFilters) => {
        const { loading, error, data } = useQuery(getAvgActionPlansPerUserResponse, {
            fetchPolicy: "no-cache",
            variables: {
                args: {
                    max_date: filters.date,
                    ...(filters?.meetingTypeIds &&
                        !filters.meetingTypeIds.includes(notSelected) &&
                        filters.totalMeetingTypes &&
                        filters.meetingTypeIds.length !== filters.totalMeetingTypes && {
                            meeting_type_ids: `{${filters.meetingTypeIds}}`,
                        }),
                    ...(filters?.meetingTypeIds &&
                        filters.meetingTypeIds.includes(notSelected) && {
                            meeting_type_null: true,
                        }),
                },
            },
        });

        const [apAvgPerUser, setApAvgPerUser] = useState<IAPAvgPerUser[]>([]);

        useEffect(() => {
            if (data) {
                setApAvgPerUser(data.avg_action_plans_per_user_by_date);
            }
        }, [data]);

        return {
            loading,
            apAvgPerUser,
            apolloQueryError: error,
        };
    },
    GetRcaMethodologyUsedByUser: (filters: IArgsFilters) => {
        const { loading, error, data } = useQuery(getRcaMethodologyUsedByUserResponse, {
            fetchPolicy: "no-cache",
            variables: {
                args: {
                    max_date: filters.date,
                    ...(filters?.meetingTypeIds &&
                        !filters.meetingTypeIds.includes(notSelected) &&
                        filters.totalMeetingTypes &&
                        filters.meetingTypeIds.length !== filters.totalMeetingTypes && {
                            meeting_type_ids: `{${filters.meetingTypeIds}}`,
                        }),
                    ...(filters?.meetingTypeIds &&
                        filters.meetingTypeIds.includes(notSelected) && {
                            meeting_type_null: true,
                        }),
                },
            },
        });

        const [rcaMethodologyUsedByUser, setRcaMethodologyUsedByUser] = useState<IRcaMethodologyUsedByUser[]>([]);

        useEffect(() => {
            if (data) {
                setRcaMethodologyUsedByUser(data.rca_methodology_used_by_user);
            }
        }, [data]);

        return {
            loading,
            rcaMethodologyUsedByUser,
            apolloQueryError: error,
        };
    },
};
